import { initContract } from "@ts-rest/core";
import { StreamableFile } from "@nestjs/common";
import { z } from "zod";
import {
    nativeInvoiceDraftBodySchema,
    nativeInvoiceDuplicateBodySchema,
    nativeInvoiceLogoUploadUrlBodySchema,
    nativeInvoiceLogoUploadUrlResponseSchema,
    nativeInvoicePreviewResponseSchema,
    nativeInvoiceResendBodySchema,
    nativeInvoiceScheduleBodySchema,
    nativeInvoiceSendBodySchema,
    nativeInvoiceTestSendBodySchema,
    nativeInvoiceUpdateBodySchema,
} from "../schemas/domains/invoice/native-invoice.schema";
import { InvoiceBase } from "../types/invoice";
import { NativeInvoiceDetailBase } from "../types/nativeInvoice";
import {
    LastOutboundEmail,
    OutboundEmailBlockReason,
} from "../types/domains/outboundEmail";

const c = initContract();

interface OutboundEmailErrorBody {
    message: string;
    blockReason?: OutboundEmailBlockReason;
}

interface NativeInvoiceSendResponse {
    nativeDetail: NativeInvoiceDetailBase;
    lastOutboundEmail: LastOutboundEmail | null;
}

// Test-send does not touch nativeDetail or lastOutboundEmail (no status
// change, no send-slot claim, no customer-facing send/resend field writes) —
// it only reports the outbox row created for the actor-addressed copy.
interface NativeInvoiceTestSendResponse {
    lastOutboundEmail: LastOutboundEmail | null;
}

const NativeInvoiceEntityPathParamsSchema = z.object({
    workspaceId: z.string().uuid(),
    entityId: z.coerce.number().int(),
});

const NativeInvoicePathParamsSchema = z.object({
    workspaceId: z.string().uuid(),
    entityId: z.coerce.number().int(),
    invoiceId: z.string().uuid(),
});

export const nativeInvoiceContract = c.router(
    {
        createNativeInvoice: {
            method: "POST",
            path: "/:workspaceId/:entityId/native",
            responses: {
                201: c.type<{
                    data: InvoiceBase;
                    nativeDetail: NativeInvoiceDetailBase;
                }>(),
                400: c.type<{ message: string }>(),
                404: c.type<object>(),
            },
            body: nativeInvoiceDraftBodySchema,
            pathParams: NativeInvoiceEntityPathParamsSchema,
        },
        updateNativeInvoice: {
            method: "PATCH",
            path: "/:workspaceId/:entityId/native/:invoiceId",
            responses: {
                200: c.type<{
                    data: InvoiceBase;
                    nativeDetail: NativeInvoiceDetailBase;
                }>(),
                400: c.type<{ message: string }>(),
                404: c.type<object>(),
            },
            body: nativeInvoiceUpdateBodySchema,
            pathParams: NativeInvoicePathParamsSchema,
        },
        previewNativeInvoice: {
            method: "GET",
            path: "/:workspaceId/:entityId/native/:invoiceId/preview",
            responses: {
                200: nativeInvoicePreviewResponseSchema,
                400: c.type<{ message: string }>(),
                404: c.type<object>(),
            },
            pathParams: NativeInvoicePathParamsSchema,
        },
        downloadNativeInvoicePdf: {
            method: "GET",
            path: "/:workspaceId/:entityId/native/:invoiceId/pdf",
            responses: {
                200: c.type<StreamableFile>(),
                400: c.type<{ message: string }>(),
                404: c.type<object>(),
            },
            pathParams: NativeInvoicePathParamsSchema,
        },
        sendNativeInvoice: {
            method: "POST",
            path: "/:workspaceId/:entityId/native/:invoiceId/send",
            responses: {
                200: c.type<NativeInvoiceSendResponse>(),
                400: c.type<OutboundEmailErrorBody>(),
                403: c.type<OutboundEmailErrorBody>(),
                404: c.type<object>(),
                429: c.type<OutboundEmailErrorBody>(),
                503: c.type<OutboundEmailErrorBody>(),
            },
            body: nativeInvoiceSendBodySchema,
            pathParams: NativeInvoicePathParamsSchema,
        },
        resendNativeInvoice: {
            method: "POST",
            path: "/:workspaceId/:entityId/native/:invoiceId/resend",
            responses: {
                200: c.type<NativeInvoiceSendResponse>(),
                400: c.type<OutboundEmailErrorBody>(),
                403: c.type<OutboundEmailErrorBody>(),
                404: c.type<object>(),
                429: c.type<OutboundEmailErrorBody>(),
                503: c.type<OutboundEmailErrorBody>(),
            },
            body: nativeInvoiceResendBodySchema,
            pathParams: NativeInvoicePathParamsSchema,
        },
        // Emails the requesting user a copy of the draft (PDF + pay link when
        // enabled) without changing status, claiming the send slot, archiving
        // the PDF, or writing any customer-facing send/resend field. Available
        // on any accessible native invoice regardless of status.
        testSendNativeInvoice: {
            method: "POST",
            path: "/:workspaceId/:entityId/native/:invoiceId/test-send",
            responses: {
                200: c.type<NativeInvoiceTestSendResponse>(),
                400: c.type<OutboundEmailErrorBody>(),
                403: c.type<OutboundEmailErrorBody>(),
                404: c.type<object>(),
                429: c.type<OutboundEmailErrorBody>(),
                503: c.type<OutboundEmailErrorBody>(),
            },
            body: nativeInvoiceTestSendBodySchema,
            pathParams: NativeInvoicePathParamsSchema,
        },
        scheduleNativeInvoice: {
            method: "POST",
            path: "/:workspaceId/:entityId/native/:invoiceId/schedule",
            responses: {
                200: c.type<NativeInvoiceSendResponse>(),
                400: c.type<OutboundEmailErrorBody>(),
                403: c.type<OutboundEmailErrorBody>(),
                404: c.type<object>(),
                429: c.type<OutboundEmailErrorBody>(),
                503: c.type<OutboundEmailErrorBody>(),
            },
            body: nativeInvoiceScheduleBodySchema,
            pathParams: NativeInvoicePathParamsSchema,
        },
        duplicateNativeInvoice: {
            method: "POST",
            path: "/:workspaceId/:entityId/native/:invoiceId/duplicate",
            responses: {
                201: c.type<{
                    data: InvoiceBase;
                    nativeDetail: NativeInvoiceDetailBase;
                }>(),
                400: c.type<{ message: string }>(),
                404: c.type<object>(),
            },
            body: nativeInvoiceDuplicateBodySchema,
            pathParams: NativeInvoicePathParamsSchema,
        },
        createNativeInvoiceLogoUploadUrl: {
            method: "POST",
            path: "/:workspaceId/:entityId/native/logo/upload-url",
            responses: {
                201: nativeInvoiceLogoUploadUrlResponseSchema,
                400: c.type<{ message: string }>(),
                404: c.type<object>(),
            },
            body: nativeInvoiceLogoUploadUrlBodySchema,
            pathParams: NativeInvoiceEntityPathParamsSchema,
        },
    },
    {
        pathPrefix: "/invoices",
    },
);
